import { cn, convertToSlug } from '@sohanemon/utils';
import { Iconify } from '@sohanemon/utils/components';
import { useCopyToClipboard } from '@sohanemon/utils/hooks';
import { Button } from '@sohanscript/ui/button';
import {
	Children,
	type ComponentPropsWithoutRef,
	type ComponentType,
	cloneElement,
	createElement,
	isValidElement,
	type LazyExoticComponent,
	lazy,
	type ReactNode,
	Suspense,
	useMemo,
} from 'react';
import { Pre } from './code-block';
import { Embed } from './embed';
import {
	MarkdownImage,
	type RenderImage,
	type RenderImageProps,
} from './markdown-image';
import {
	ArchInstall,
	Install,
	LinuxInstall,
	NpmInstall,
} from './package-install';
import { CodeEditor, CodeTabs } from './project-code-block';

type HeadingTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

type HeadingProps = ComponentPropsWithoutRef<HeadingTag> & { id?: string };

/**
 * Heading ids are produced by the markdown parser, so anchors always match the
 * ids the table of contents is generated from.
 */
function makeHeading(Tag: HeadingTag) {
	return function Heading({ id, className, children, ...props }: HeadingProps) {
		const { copy, isCopied } = useCopyToClipboard({ timeout: 2000 });
		const anchorId = id ?? convertToSlug(String(children ?? ''));
		return (
			<Tag
				id={anchorId}
				className={cn(
					'scroll-mt-32 font-futura text-foreground-light',
					className,
				)}
				{...props}
			>
				{children}{' '}
				<Iconify
					icon={isCopied ? 'lucide:check' : 'lucide:link'}
					className={cn(
						'inline-block transition-colors',
						!isCopied && 'size-5 not-hover:opacity-30',
					)}
					role="button"
					onClick={() =>
						copy(`${location.origin}${location.pathname}#${anchorId}`)
					}
				/>
			</Tag>
		);
	};
}

// Stable heading components — new references on every render would remount
// heading elements, producing flicker and broken scroll-spy.
const HEADINGS: Record<HeadingTag, (props: HeadingProps) => ReactNode> = {
	h1: makeHeading('h1'),
	h2: makeHeading('h2'),
	h3: makeHeading('h3'),
	h4: makeHeading('h4'),
	h5: makeHeading('h5'),
	h6: makeHeading('h6'),
};

type DirectiveProps = Record<string, string> & { children?: ReactNode };
type DirectiveComponent = (props: DirectiveProps) => ReactNode;

const DIRECTIVES = {
	'npm-install': NpmInstall,
	'linux-install': LinuxInstall,
	'arch-install': ArchInstall,
	install: Install,
	embed: Embed,
	editor: CodeEditor,
	tabs: CodeTabs,
} as unknown as Record<string, DirectiveComponent>;

type MarkdownComponentsOptions = {
	imgBase?: string;
	components?: MarkdownComponentRegistry;
	/**
	 * Renders image elements instead of the default plain `<img>`. Lets the
	 * host app plug its own image component (lazy loading, CDN resizing, …).
	 */
	renderImage?: RenderImage;
};

export type MarkdownComponentLoader = () => Promise<Record<string, unknown>>;
export type MarkdownComponentRegistry = Record<string, MarkdownComponentLoader>;

export type { RenderImage, RenderImageProps };

/**
 * Routes a parsed `:::name{…}` directive to its component. Attributes arrive as
 * props, matching the directive syntax used by the site content.
 */
function MarkdownDirective({
	'data-component': name,
	'data-attributes': _attributes,
	customComponents,
	...props
}: {
	'data-component'?: string;
	'data-attributes'?: string;
	customComponents?: MarkdownComponentRegistry;
	[key: string]: unknown;
}) {
	if (name === 'component') {
		const customName = props.name;
		if (typeof customName !== 'string') return null;
		const loadComponent = customComponents?.[customName];
		if (!loadComponent) return null;
		const exportName =
			typeof props.export === 'string' ? props.export : 'default';
		const {
			name: _customName,
			export: _exportName,
			children: _children,
			...componentProps
		} = props;
		const Component = getLazyComponent(customName, exportName, loadComponent);
		return (
			<Suspense
				fallback={
					<div
						aria-hidden="true"
						className="my-6 min-h-24 animate-pulse rounded-lg bg-muted/20 motion-reduce:animate-none"
					/>
				}
			>
				{createElement(Component, componentProps)}
			</Suspense>
		);
	}

	const Component = name ? DIRECTIVES[name] : undefined;
	if (!Component) return null;
	return <Component {...props} />;
}

type MarkdownDemoProps = Record<string, unknown>;
type LazyMarkdownDemo = LazyExoticComponent<ComponentType<MarkdownDemoProps>>;

const lazyMarkdownDemos = new Map<string, LazyMarkdownDemo>();

function MissingMarkdownDemo(_props: MarkdownDemoProps) {
	return null;
}

function isMarkdownComponent(
	value: unknown,
): value is ComponentType<MarkdownDemoProps> {
	return (
		typeof value === 'function' ||
		(typeof value === 'object' && value !== null && '$$typeof' in value)
	);
}

function getLazyComponent(
	name: string,
	exportName: string,
	loadComponent: MarkdownComponentLoader,
) {
	const cacheKey = `${name}:${exportName}`;
	const cached = lazyMarkdownDemos.get(cacheKey);
	if (cached) return cached;

	const component = lazy(async () => {
		const module = await loadComponent();
		const exported = module[exportName];
		return {
			default: isMarkdownComponent(exported) ? exported : MissingMarkdownDemo,
		};
	});
	lazyMarkdownDemos.set(cacheKey, component);
	return component;
}

/**
 * TanStack Markdown renders task list items without the GitHub class names, so
 * the classes the prose stylesheet and user snippets rely on are restored by
 * looking for the checkbox the parser emits.
 */
function containsCheckbox(children: ReactNode): boolean {
	return Children.toArray(children).some((child) => {
		if (!isValidElement<{ type?: string; children?: ReactNode }>(child)) {
			return false;
		}
		if (child.type === 'input' && child.props.type === 'checkbox') return true;
		return containsCheckbox(child.props.children);
	});
}

function TaskList({ className, ...props }: ComponentPropsWithoutRef<'ul'>) {
	const isTaskList = containsCheckbox(props.children);
	return (
		<ul
			className={cn(isTaskList && 'contains-task-list', className)}
			{...props}
		/>
	);
}

function TaskListItem({ className, ...props }: ComponentPropsWithoutRef<'li'>) {
	const isTaskItem = containsCheckbox(props.children);
	return (
		<li className={cn(isTaskItem && 'task-list-item', className)} {...props} />
	);
}

/**
 * Images nested in a link stay plain: a button trigger inside an anchor is
 * invalid markup and steals the click from the link.
 */
function withoutInspection(
	children: ReactNode,
	imageType: React.ElementType,
): ReactNode {
	return Children.map(children, (child) =>
		isValidElement<{ inspect?: boolean }>(child) && child.type === imageType
			? cloneElement(child, { inspect: false })
			: child,
	);
}

function isFigcaption(child: ReactNode): boolean {
	return (
		typeof child === 'object' &&
		child !== null &&
		'type' in child &&
		child.type === 'figcaption'
	);
}

/**
 * Fences carrying a `title=` / `file=` meta arrive wrapped in a figure with a
 * figcaption. The code block header already shows that title, so the caption is
 * dropped instead of rendering it twice.
 */
function CodeFigure({ children }: ComponentPropsWithoutRef<'figure'>) {
	const panels = Array.isArray(children)
		? children.filter((child) => !isFigcaption(child))
		: children;
	return <>{panels}</>;
}

/**
 * Parser props for an image, plus the inspection switch `withoutInspection`
 * clones onto linked images.
 */
type MarkdownImageElementProps = ComponentPropsWithoutRef<'img'> & {
	inspect?: boolean;
};

/**
 * Reads the `?class=` contract, resolves the src against `imgBase` and hands
 * the parser props to the image renderer.
 */
function resolveImageProps(
	{ src, className, alt, ...rest }: MarkdownImageElementProps,
	imgBase?: string,
): (RenderImageProps & { inspect?: boolean }) | null {
	if (!src) return null;

	// NOTE: a base-less `new URL` throws for relative src, so the `?class=`
	// contract has to be read from the raw string to work for local images.
	const hashIndex = src.indexOf('#');
	const hash = hashIndex === -1 ? '' : src.slice(hashIndex);
	const withoutHash = hashIndex === -1 ? src : src.slice(0, hashIndex);
	const queryIndex = withoutHash.indexOf('?');
	let urlClassName = '';
	if (queryIndex !== -1) {
		const params = new URLSearchParams(withoutHash.slice(queryIndex + 1));
		const classParam = params.get('class');
		if (classParam !== null) {
			params.delete('class');
			const query = params.toString();
			src = `${withoutHash.slice(0, queryIndex)}${query ? `?${query}` : ''}${hash}`;
			urlClassName = classParam;
		}
	}

	const resolved = imgBase
		? /^https?:\/\//.test(src)
			? src
			: new URL(src.replace(/^\.\//, ''), imgBase).href
		: src;

	return {
		src: resolved,
		className: cn(urlClassName, className),
		alt,
		...rest,
	};
}

function getMarkdownComponents({
	imgBase,
	renderImage,
	components: customComponents,
}: MarkdownComponentsOptions) {
	function img(props: MarkdownImageElementProps) {
		const resolved = resolveImageProps(props, imgBase);
		if (!resolved) return null;
		return <MarkdownImage {...resolved} renderImage={renderImage} />;
	}

	return {
		...HEADINGS,
		pre: Pre,
		figure: CodeFigure,
		ul: TaskList,
		li: TaskListItem,
		'md-comment-component': (props: Record<string, unknown>) => (
			<MarkdownDirective {...props} customComponents={customComponents} />
		),
		a: ({ href, children, ...props }: ComponentPropsWithoutRef<'a'>) => {
			if (!href) return null;
			const isExternal = /^https?:\/\//.test(href);
			const isPlainText = typeof children === 'string';

			// NOTE: only HTTP(S) links with plain text become link buttons. Hash
			// links (footnote refs/backrefs, heading anchors) and relative links
			// must stay real in-page anchors, otherwise target="_blank" turns them
			// into new tabs and the footnote backref overlay nests anchors.
			if (!(isExternal && isPlainText)) {
				return (
					<a
						href={href}
						target={isExternal ? '_blank' : undefined}
						rel={isExternal ? 'noopener noreferrer' : undefined}
						{...props}
					>
						{withoutInspection(children, img)}
					</a>
				);
			}

			return (
				<Button
					variant="link"
					size="auto"
					href={href}
					target="_blank"
					rel="noopener noreferrer"
					className={props.className}
					title={props.title}
					style={{ fontSize: 'inherit' }}
				>
					{children}
					<Iconify
						icon="lucide:external-link"
						className="size-3.5 shrink-0 opacity-60"
					/>
				</Button>
			);
		},
		img,
	};
}

export const useMarkdownComponents = ({
	imgBase,
	renderImage,
	components,
}: MarkdownComponentsOptions) =>
	useMemo(
		() => getMarkdownComponents({ imgBase, renderImage, components }),
		[imgBase, renderImage, components],
	);
